import { UpsertActivityDialogProps } from "@/app/activity/components/upsertActivityDialog/type"

import { createActivityStore } from "@/app/activity/store"
import Dialog from "@/components/dialog/Dialog"

import { Activity, ActivityTypeKey, ActivityType, UpsertPlayerRpePayload } from "@/types/activity"

import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { cloneDeep, isEqual } from "lodash"

import "./upsertActivityDialog.scss"
import {
  useCreateActivityMutation,
  useDeleteActivityMutation,
  usePlayerRpeQuery,
  useUpsertActivityMutation,
  useUpsertPlayersRpeMutation,
} from "@/hooks/api/useActivityQuery"
import useRouter from "@/hooks/router/useRoute"
import BasicButton from "@/components/button/BasicButton"
import TabActivityInfo from "@/app/activity/components/upsertActivityDialog/TabActivityInfo/TabActivityInfo"
import Tab from "@/components/tab/Tab"
import TabPlayerRpe from "@/app/activity/components/upsertActivityDialog/TabPlayerRpe/TabPlayerRpe"
import { PlayerData } from "@/types/player"
import { playerRpeListSort } from "@/utils/sort"
import { useSearchParams } from "@/hooks/useSearchParams"
import { createGlobalStore } from "@/stores/global"

export default function UpsertActivityDialog({
  open,
  onClose,
  isDetail = false,
}: UpsertActivityDialogProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const { activity, setTempActivity, tempActivity, setActivity } = createActivityStore((state) => ({
    activity: state.activity,
    setActivity: state.setTempActivity,
    setTempActivity: state.setTempActivity,
    tempActivity: state.tempActivity,
  }))

  const { setSnackBar } = createGlobalStore((state) => ({ setSnackBar: state.setSnackBar }))

  const params = useSearchParams()

  const [activityId, setActivityId] = useState<number>()
  const [isDurationInValid, setIsDurationInValid] = useState(false)
  const [durationErrMessage, setDurationErrMessage] = useState("")
  const [tabValue, setTabValue] = useState("general")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [submitted, setSubmitted] = useState(false)

  const { mutateAsync: createActivityMutation } = useCreateActivityMutation()
  const { mutateAsync: updateActivityMutation } = useUpsertActivityMutation(activityId)
  const { mutateAsync: deleteActivityMutation } = useDeleteActivityMutation(activityId)
  const { mutateAsync: savePlayerRpeMutation } = useUpsertPlayersRpeMutation()

  const [playerRpeList, setPlayerRpeList] = useState<PlayerData[]>([])

  const { data: participatedPlayerList } = usePlayerRpeQuery(activityId as number)

  participatedPlayerList?.sort(playerRpeListSort)

  const isDirty = useMemo(() => {
    const isActivityEqual = !!activity && !!tempActivity && !isEqual(activity, tempActivity)
    const isPlayerRpeEqual =
      !!participatedPlayerList && !!playerRpeList && !isEqual(participatedPlayerList, playerRpeList)

    return isActivityEqual || isPlayerRpeEqual
  }, [tempActivity, activity, participatedPlayerList, playerRpeList])

  const titleErrorMsg = useMemo(() => {
    if (!tempActivity?.name || tempActivity.name.trim() === "") {
      return t("rules.required")
    }

    if (tempActivity.name.length > 35) {
      return t("rules.max_len", { name: t("name"), max_len: 35 })
    }

    return ""
  }, [tempActivity?.name, t])

  const typeErrorMsg = useMemo(() => {
    if (!tempActivity?.type) {
      return t("rules.required")
    }

    return ""
  }, [tempActivity?.type, t])

  const matchDayErrorMsg = useMemo(() => {
    if (!tempActivity?.matchDay) {
      return t("rules.required")
    }

    return ""
  }, [tempActivity?.matchDay, t])

  const isInvalidForm = useMemo(() => {
    return (
      isDurationInValid ||
      !!titleErrorMsg ||
      !!typeErrorMsg ||
      !!matchDayErrorMsg ||
      !tempActivity?.name ||
      tempActivity.name.trim() === "" ||
      !tempActivity?.type ||
      !tempActivity?.matchDay
    )
  }, [
    isDurationInValid,
    titleErrorMsg,
    typeErrorMsg,
    matchDayErrorMsg,
    tempActivity?.name,
    tempActivity?.type,
    tempActivity?.matchDay,
  ])

  const tabList = useMemo(
    () => [
      { label: t("general"), value: "general", disabled: isInvalidForm },
      { label: t("player"), value: "player", disabled: isInvalidForm },
    ],
    [isInvalidForm],
  )

  const onCloseDialog = () => {
    if (isDirty && !window.confirm(t("messages.confirmLeavePage"))) {
      return
    }
    window.removeEventListener("beforeunload", handleBeforeUnload)
    setTabValue("general")
    setTouched({})
    setSubmitted(false)

    if (isDetail && participatedPlayerList) {
      participatedPlayerList.sort(playerRpeListSort)
      setPlayerRpeList(cloneDeep(participatedPlayerList))
    }

    if (isDetail) {
      setTempActivity({ ...activity } as Activity)
    } else {
      setTempActivity()
      setActivity()
    }
    onClose()
  }

  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    if (isDirty) {
      e.preventDefault()
      e.returnValue = ""
    }
  }

  const saveActivityWrapper = async () => {
    try {
      createActivity()
    } catch (e) {
      console.error(e)
    }

    window.removeEventListener("beforeunload", handleBeforeUnload)
    setTabValue("general")
    setTouched({})
    setSubmitted(false)

    if (isDetail && participatedPlayerList) {
      participatedPlayerList.sort(playerRpeListSort)
      setPlayerRpeList(cloneDeep(participatedPlayerList))
    }

    if (!isDetail) {
      setTempActivity()
    }
    onClose()
  }

  const createActivity = async () => {
    const { dateStart, dateFinish, type, matchDay, memo, idfStadium, name } =
      tempActivity as Activity

    const activityType =
      type in ActivityType ? type : ActivityTypeKey[type as keyof typeof ActivityTypeKey]

    const payload = {
      dateStart: new Date(dateStart).toISOString(),
      dateFinish: new Date(dateFinish).toISOString(),
      type: activityType,
      matchDay,
      memo,
      idfStadium,
      name,
    }

    const { response } = await createActivityMutation(payload as Activity)
    if (response !== undefined) {
      router?.push(`/activity/detail?activity=${response}`)
    }

    setActivity({ ...tempActivity, idfActivity: response as number } as Activity)
    setSnackBar({
      open: true,
      message: t("messages.successAdd"),
      severity: "success",
    })
  }

  const saveRpe = async () => {
    const activityPlayerEditList = cloneDeep(playerRpeList)
    const payload = { activityPlayerEditList } as unknown as UpsertPlayerRpePayload

    return await savePlayerRpeMutation({ activityId: activityId as number, payload })
  }

  const onOpenDeleteDialog = () => {
    setDeleteDialogOpen(true)
  }

  const onCloseDeleteDialog = () => {
    setDeleteDialogOpen(false)
  }

  const deleteActivity = async () => {
    if (!activityId) {
      return
    }

    try {
      onCloseDeleteDialog()
      onClose()

      router?.replace("/activity/list")

      await deleteActivityMutation(activityId)
      setSnackBar({
        open: true,
        message: t("messages.successDelete"),
        severity: "success",
      })
    } catch (e) {
      console.error(e)
    }
  }

  const onClickHandler = async () => {
    if (isInvalidForm) {
      setSubmitted(true)
      return
    }

    if (!isDetail) {
      await saveActivityWrapper()

      return
    }

    if (isDetail && tabValue === "player") {
      try {
        await Promise.all([
          updateActivityMutation({
            idfActivity: (tempActivity as Activity).idfActivity as number,
            payload: tempActivity as Activity,
          }),
          saveRpe(),
        ])

        window.removeEventListener("beforeunload", handleBeforeUnload)

        setSnackBar({
          open: true,
          message: t("messages.successUpdate"),
          severity: "success",
        })

        if (!isDetail) {
          setTempActivity()
        }
        setTabValue("general")
        onClose()
      } catch (e) {
        console.error(e)
      }

      return
    }

    if (isDetail && tabValue === "general") {
      setTabValue("player")
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: string | number) => {
    if (newValue !== tabValue) {
      switch (tabValue) {
        case "general":
          setTabValue("player")
          break
        case "player":
          setTabValue("general")
          break
      }
    }
  }

  useEffect(() => {
    const idfActivity = params.get("activity")

    if (idfActivity) {
      setActivityId(Number(idfActivity))
    }
  }, [params])

  useEffect(() => {
    setTempActivity({ ...activity } as Activity)
  }, [activity])

  useEffect(() => {
    if (isDirty) {
      window.addEventListener("beforeunload", handleBeforeUnload)
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [isDirty])

  useEffect(() => {
    if (!participatedPlayerList) {
      return
    }
    participatedPlayerList.sort(playerRpeListSort)
    setPlayerRpeList(cloneDeep(participatedPlayerList))
  }, [participatedPlayerList])

  return (
    <>
      <Dialog
        open={open}
        onClose={onCloseDialog}
        title={t(tempActivity?.idfActivity ? "editActivity" : "createActivity")}
        maxWidth="900px"
        maxHeight="80vh"
        onConfirm={onClickHandler}
        footer={
          <div className="footer-btn-cont">
            <div className="footer-basic-btn">
              <BasicButton onClick={onCloseDialog} size="md" type={"default-outlined"}>
                {t("button.cancel")}
              </BasicButton>
              <BasicButton
                onClick={onClickHandler}
                color="primary"
                size="md"
                type="primary-contained">
                {t(`button.${!isDetail || tabValue === "player" ? "save" : "next"}`)}
              </BasicButton>
            </div>
            {isDetail && (
              <BasicButton
                color="primary"
                size="md"
                type="danger-outlined"
                onClick={onOpenDeleteDialog}>
                {t("button.delete")}
              </BasicButton>
            )}
          </div>
        }>
        {isDetail ? (
          <>
            <Tab
              className="activity-dialog-rpe-tab"
              centered
              type="filled"
              list={tabList}
              value={tabValue}
              onChange={handleTabChange}></Tab>
            {tabValue === "general" && (
              <TabActivityInfo
                setDurationErrMessage={setDurationErrMessage}
                setIsDurationInValid={setIsDurationInValid}
                isDurationInValid={isDurationInValid}
                durationErrMessage={durationErrMessage}
                titleErrorMsg={titleErrorMsg}
                typeErrorMsg={typeErrorMsg}
                matchDayErrorMsg={matchDayErrorMsg}
                touched={touched}
                setTouched={setTouched}
                submitted={submitted}
              />
            )}
            {tabValue === "player" && (
              <TabPlayerRpe playerRpeList={playerRpeList} updatePlayerRpeList={setPlayerRpeList} />
            )}
          </>
        ) : (
          <TabActivityInfo
            setDurationErrMessage={setDurationErrMessage}
            setIsDurationInValid={setIsDurationInValid}
            isDurationInValid={isDurationInValid}
            durationErrMessage={durationErrMessage}
            titleErrorMsg={titleErrorMsg}
            typeErrorMsg={typeErrorMsg}
            matchDayErrorMsg={matchDayErrorMsg}
            touched={touched}
            setTouched={setTouched}
            submitted={submitted}
          />
        )}
      </Dialog>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        title={t("confirm")}
        open={deleteDialogOpen}
        onClose={onCloseDeleteDialog}
        maxWidth="400px"
        footer={
          <div className="confirm-btn-cont">
            <BasicButton type="default-outlined" size="md" onClick={onCloseDeleteDialog}>
              {t("button.cancel")}
            </BasicButton>
            <BasicButton type="danger-outlined" size="md" onClick={deleteActivity}>
              {t("button.delete")}
            </BasicButton>
          </div>
        }>
        <p>{t("messages.confirmDelete")}</p>
      </Dialog>
    </>
  )
}
